import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  DollarSign, 
  Users, 
  Zap, 
  Shield, 
  TrendingUp, 
  Headphones,
  ChevronRight,
  CheckCircle,
  Globe,
  Palette,
  LineChart,
  Award,
  ArrowRight
} from 'lucide-react';

const benefits = [
  {
    icon: DollarSign,
    title: 'Generous Commissions',
    description: 'Earn up to 30% recurring commission on every subscription you refer. Build passive income with our competitive revenue share model.',
    color: 'emerald'
  },
  {
    icon: Palette,
    title: 'White-Label Ready',
    description: 'Rebrand the entire platform with your logo, colors, and domain. Your clients will never know we exist.',
    color: 'purple'
  },
  {
    icon: Users,
    title: 'Multi-Tier Accounts',
    description: 'Create and manage sub-accounts for your clients. Each gets their own dashboard, AI agents, and analytics.',
    color: 'blue'
  },
  {
    icon: Zap,
    title: 'Instant Deployment',
    description: 'Deploy AI agents for clients in minutes, not days. Our no-code builder makes it easy to deliver value fast.',
    color: 'amber'
  },
  {
    icon: LineChart,
    title: 'Revenue Analytics',
    description: 'Track your earnings, client growth, and performance metrics in real-time from your reseller dashboard.',
    color: 'indigo'
  },
  {
    icon: Headphones,
    title: 'Priority Support',
    description: 'Get dedicated account management, priority support, and direct access to our engineering team.',
    color: 'pink'
  }
];

const tiers = [
  {
    name: 'Starter Reseller',
    commission: '15%',
    features: ['Up to 10 client accounts', 'Basic white-labeling', 'Email support', 'Monthly payouts'],
    price: 'Free',
    popular: false
  },
  {
    name: 'Professional Reseller',
    commission: '25%',
    features: ['Up to 50 client accounts', 'Full white-labeling', 'Priority support', 'Bi-weekly payouts', 'Custom domain support', 'API access'],
    price: '$99/mo',
    popular: true
  },
  {
    name: 'Enterprise Partner',
    commission: '30%',
    features: ['Unlimited client accounts', 'Complete white-label', 'Dedicated account manager', 'Weekly payouts', 'Custom integrations', 'SLA guarantee', 'Co-marketing opportunities'],
    price: 'Custom',
    popular: false
  }
];

export const ResellerBenefitsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.png" alt="Harmonix" className="h-8 w-auto" />
            <span className="font-bold text-xl text-slate-900">Harmonix</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600">Sign In</Button>
            </Link>
            <Link to="/login">
              <Button className="bg-teal-600 hover:bg-teal-700">Become a Reseller</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 border border-blue-200 mb-6">
            <Award className="w-4 h-4 text-slate-900" />
            <span className="text-sm font-medium text-slate-900">Reseller Partner Program</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Grow Your Business with{' '}
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              AI Employees
            </span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Partner with Harmonix to offer cutting-edge AI agent solutions to your clients.
            Earn recurring revenue while we handle the technology.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/login">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 px-8">
                Apply Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-slate-300 px-8">
              Download Brochure
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-6 border-y border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-3xl md:text-4xl font-bold text-slate-900">30%</div>
            <div className="text-sm text-slate-600 mt-1">Max Commission</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-emerald-600">500+</div>
            <div className="text-sm text-slate-600 mt-1">Active Resellers</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-purple-600">$2M+</div>
            <div className="text-sm text-slate-600 mt-1">Paid to Partners</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-amber-600">98%</div>
            <div className="text-sm text-slate-600 mt-1">Partner Satisfaction</div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Partner with Harmonix?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to build a successful AI agent business
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={`benefit-${benefit.title}-${index}`} className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-${benefit.color}-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-6 h-6 text-${benefit.color}-600`} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Reseller Tiers</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the partnership level that fits your business
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <Card 
                key={tier.name} 
                className={`bg-white relative ${tier.popular ? 'border-2 border-teal-600 shadow-xl' : 'border-slate-200'}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-teal-600 text-white text-sm font-medium rounded-full">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{tier.name}</h3>
                    <div className="text-4xl font-bold text-slate-900 mb-1">{tier.commission}</div>
                    <p className="text-slate-500 text-sm">recurring commission</p>
                    <div className="mt-4 text-2xl font-semibold text-slate-900">{tier.price}</div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, i) => (
                      <li key={`tier-${tier.name}-feature-${i}`} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${tier.popular ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get started in three simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Apply', description: 'Complete our simple application form. We review and approve within 24 hours.' },
              { step: '02', title: 'Set Up', description: 'Get your white-label dashboard, training materials, and marketing assets.' },
              { step: '03', title: 'Earn', description: 'Start selling to your clients and earn recurring commissions every month.' }
            ].map((item) => (
              <div key={`how-it-works-${item.step}`} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-600 to-cyan-600 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Grow Your Revenue?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join 500+ resellers already earning with Harmonix
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/login">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-50 px-8">
                Apply Now
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">
              Schedule Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/favicon.png" alt="Harmonix" className="h-6 w-auto" />
            <span className="font-semibold text-slate-900">Harmonix</span>
          </div>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Harmonix. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <Link to="/legal/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/login" className="hover:text-slate-900">Partner Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResellerBenefitsPage;
